import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import AuthService from "../../Services/auth/AuthService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(AuthService.isAuthenticated());
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [accountSecurity, setAccountSecurity] = useState(false);
  const [error, setError] = useState('');

  const register = useCallback(async (userData) => {
    setLoading(true);
    setError('');
    try {
      const { success, data, error } = await AuthService.register(userData);
      if (!success) {
        setError(error);
      }
      return { success, data, error };
    } catch (error) {
      const errorMessage = error.message || 'An error occurred during registration';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const initialRegister = useCallback(async (userData) => {
    setLoading(true);
    setError('');
    try {
      const { success, data, error } = await AuthService.initialRegister(userData);
      if (!success) {
        setError(error);
      }
      return { success, data, error };
    } catch (error) {
      const errorMessage = error.message || 'An error occurred during initial registration';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const getUser = useCallback(async () => {
    if (!AuthService.isAuthenticated()) {
      // console.log("getUser: User is not authenticated");
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const { success, user, error } = await AuthService.getUser();
      // console.log("getUser response:", { success, user, error });

      if (success) {
        // console.log("User data fetched successfully:", user); 
        setUserData(user);
        return user;
      } else {
        setError(error);
        console.error("Failed to fetch user data:", error);
        return null;
      }
    } catch (error) {
      setError(error);
      console.error("Error in getUser:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const setEducationalLevel = useCallback(async (educationalData) => {
    setLoading(true);
    setError('');
    try {
      const { success, data, error } = await AuthService.setEducationalLevel(educationalData);
      
      if (success) {
        // console.log("Educational level set successfully, refreshing user data");
        
        // Simply fetch fresh user data with the updated educational level
        await getUser();
        
        // The updated user data will now include the educational level
        return { success: true, data };
      } else {
        setError(error);
        return { success: false, error };
      }
    } catch (error) {
      const errorMessage = error.message || 'An error occurred while setting educational level';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const login = useCallback(async (identity, password) => {
    setLoading(true);
    setError('');
    try {
      const { success, token, message, device, user } = await AuthService.login(identity, password);

      if (success && token) {
        // Authenticated at service/localStorage level, now get user data
        const fetchedUser = await getUser();
        // Finally update context state to trigger re-renders
        setAuthenticated(true);
        return { success, message, token, user: fetchedUser };
      }

      if (success && device) {
        setError('OTP sent to your email. Please verify.');
        return { success, message, device, user };
      }

      setError(message || 'Login failed');
      return { success: false, message };
    } catch (error) {
      const errorMessage = error.message || 'An error occurred during login';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const loginWithGoogle = useCallback(async (token) => {
    setLoading(true);
    setError('');
    try {
      const { success, token: authToken, message, device, user } = await AuthService.loginWithGoogle(token);

      if (success && authToken) {
        const fetchedUser = await getUser();
        setUserData(fetchedUser);
        setAuthenticated(true);

        return {
          success,
          message,
          token: authToken,
          user: fetchedUser,
          needsEducationalLevel: fetchedUser &&
            (fetchedUser.educational_level === null ||
              !fetchedUser.educational_level)
        };
      }

      if (success && device) {
        setError('OTP sent to your email. Please verify.');
        return { success, message, device, user };
      }

      setError(message || 'Google login failed');
      return { success: false, message };
    } catch (error) {
      const errorMessage = error.message || 'An error occurred during Google login';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const loginWithMagicLink = useCallback(async (token) => {
    setLoading(true);
    setError('');
    try {
      const { success, token: authToken, message } = await AuthService.magicLogin(token);

      if (success && authToken) {
        const user = await getUser();
        setAuthenticated(true);
        return { success, message, token: authToken, user };
      }

      setError(message || 'Magic link login failed');
      return { success: false, message };
    } catch (error) {
      const errorMessage = error.message || 'An error occurred during magic link login';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const loginOtp = useCallback(async (email, otp) => {
    setLoading(true);
    setError('');
    try {
      const { success, token, error } = await AuthService.loginOtp(email, otp);
      if (success && token) {
        setAccountSecurity(true);
        const user = await getUser();
        setAuthenticated(true);
        return { success: true, message: "OTP login successful", user };
      } else {
        setError(error || "OTP login failed");
        return { success: false, message: error || "OTP login failed" };
      }
    } catch (error) {
      const errorMessage = error.message || "An error occurred during OTP login";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const verifyEmail = useCallback(async (token) => {
    return AuthService.verifyEmail(token);
  }, []);

  const logout = useCallback(async () => {
    const result = await AuthService.logout();

    if (result.success) {
      setAuthenticated(false);
      setUserData(null);
      sessionStorage.removeItem('from_magic_link');
      return result;
    } else {
      setError(result.error);
      return result;
    }
  }, []);

  const resendOtp = useCallback(async (email) => {
    const result = await AuthService.resendOtp(email);
    if (!result.success) {
      setError(result.error);
    }
    return result;
  }, []);

  const checkPassword = useCallback(async (password) => {
    const isPasswordCorrect = await AuthService.checkPassword(password);
    return isPasswordCorrect;
  }, []);

  useEffect(() => {
    getUser().finally(() => setInitialized(true));
  }, [getUser]);

  const memoizedUserData = useMemo(() => userData, [userData]);

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        initialized,
        login,
        loginWithGoogle,
        loginWithMagicLink,
        logout,
        register,
        initialRegister,
        setEducationalLevel,
        getUser,
        loginOtp,
        resendOtp,
        setAuthenticated,
        verifyEmail,
        userData: memoizedUserData,
        setUserData,
        accountSecurity,
        setAccountSecurity,
        loading,
        error,
        checkPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};